/**
 * @file お子さんごとのメモ。
 *
 * @what: 「{お子さん}のメモ」という見出しと一言の説明を付けた textarea。値は親 (draft) が持つ。
 * @why: 入力欄の位置付けが分からないという指摘 (代表 2026-08-19) への対応。何を書く欄かを
 *   見出しと説明で示し、内部方針 (子に見せない等) は書かない。
 *   欄そのものには React に値を持たせない (hooks/useUncontrolledText.ts に理由がある)。
 *   親へは打鍵の最初と、そのあとは打鍵が止まってから送る。※なぜなら最初の1回を送らないと
 *   「未保存の変更があります」の段が出るのが遅れ、書き始めたのに気づかれないためである。
 *   親から来た値は、こちらが送ったものと違うときだけ欄に入れ直す。※なぜなら送った直後に
 *   同じ値が返ってくるので、そのまま入れ直すと打鍵の続きを古い値で上書きするためである。
 * @failure: 描画のみ。2000 文字の上限は maxLength で止め、サーバ側 Validate と同じ値にする。
 */

import { useCallback, useEffect, useRef } from "react";
import { useUncontrolledText } from "../hooks/useUncontrolledText";
import "./study.css";

/** 親へ送るまでの待ち (ms)。打鍵のたびに延びる。 */
const SYNC_DELAY_MS = 600;

export function MemoCard({
  learnerName,
  value,
  onChange,
}: {
  learnerName: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const { textRef, attach, put } = useUncontrolledText(value);
  const timer = useRef<number | null>(null);
  // 親へ最後に送った値である。同じものが返ってきたら欄に触らない。
  const sentRef = useRef(value);
  // 続けて打っている間か (最初の1打だけすぐ送るため)。
  const typing = useRef(false);

  const report = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    typing.current = false;
    if (sentRef.current === textRef.current) return;
    sentRef.current = textRef.current;
    onChange(textRef.current);
  }, [onChange, textRef]);

  // 外から来た値が、こちらが送ったものでも手元のものでもないときだけ入れ直す。
  useEffect(() => {
    if (value === sentRef.current || value === textRef.current) return;
    sentRef.current = value;
    put(value);
  }, [value, textRef, put]);

  useEffect(() => {
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  return (
    <section className="study-memo-card">
      <p className="study-memo-help">
        {learnerName}について、気づいたことや次にやりたいことを自由に書けます。
      </p>
      <textarea
        id="study-memo"
        ref={attach}
        aria-label={`${learnerName}のメモ`}
        maxLength={2000}
        rows={3}
        onChange={(e) => {
          textRef.current = e.target.value;
          // 書き始めの1打はすぐ送り、続けて打つ間はまとめて送る。
          if (!typing.current) {
            typing.current = true;
            sentRef.current = textRef.current;
            onChange(textRef.current);
            return;
          }
          if (timer.current !== null) window.clearTimeout(timer.current);
          timer.current = window.setTimeout(report, SYNC_DELAY_MS);
        }}
        onBlur={report}
        placeholder="例: 毎朝の机は週 4 回できている。積み木の模倣は 4 個まで"
      />
    </section>
  );
}
